import { AKComponent } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart bubble count.
 *
 * @typedef {Object} CartBubbleRefs
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 */

/**
 * @extends {AKComponent<CartBubbleRefs>}
 */
class CartBubbleComponent extends AKComponent {
  /** @type {number} */
  get currentCartCount() {
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    return parseInt(countElement?.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    if (countElement) {
      countElement.textContent = value < 100 ? String(value) : '99+';
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   * @param {boolean} animate - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    const textElement = this.refs.cartBubbleText || this.querySelector('[ref="cartBubbleText"]');

    if (countElement) {
      if (itemCount === 0) {
        countElement.classList.add('u-d-none');
      } else {
        countElement.classList.remove('u-d-none');
      }
    }
    
    if (itemCount === 0) {
      this.classList.add('u-visually-hidden');
    } else {
      this.classList.remove('u-visually-hidden');
    }

    if (itemCount > 0 && animate) {
      this.classList.add('c-cart-bubble--animating');
    } else {
      this.classList.remove('c-cart-bubble--animating');
    }

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || !textElement) return;
    await onAnimationEnd(textElement);

    this.classList.remove('c-cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const countElement = this.refs.cartBubbleCount || this.querySelector('[ref="cartBubbleCount"]');
    const visibleCount = countElement?.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-bubble')) {
  customElements.define('cart-bubble', CartBubbleComponent);
}